// ===============================================
// 💬 LIVE CHAT - SISTEMA DE CHAT EN TIEMPO REAL
// ===============================================

import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle,
  User,
  Users,
  Clock,
  Send,
  Paperclip,
  Smile,
  Phone,
  Video,
  MoreVertical,
  X,
  CheckCircle,
  AlertCircle,
  Minimize2,
  Maximize2,
  Volume2,
  VolumeX,
  UserPlus,
  Settings,
  Filter,
  Search,
  Archive,
  Star,
  Flag,
  Headphones,
  Mic,
  MicOff,
  Camera,
  CameraOff,
  Share2,
  Download,
  RotateCcw
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// ===============================================
// 🔧 INTERFACES
// ===============================================

interface ChatUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  role: 'customer' | 'technician' | 'supervisor' | 'admin';
  last_seen?: string;
  current_session?: string;
}

interface ChatMessage {
  id: string;
  chat_id: string;
  sender_id: string;
  sender_name: string;
  sender_role: 'customer' | 'technician' | 'system';
  content: string;
  type: 'text' | 'image' | 'file' | 'system' | 'audio' | 'video';
  timestamp: string;
  is_read: boolean;
  attachments?: ChatAttachment[];
  reply_to?: string;
  is_edited?: boolean;
  reactions?: ChatReaction[];
}

interface ChatAttachment {
  id: string;
  filename: string;
  url: string;
  type: 'image' | 'document' | 'audio' | 'video';
  size: number;
  thumbnail?: string;
}

interface ChatReaction {
  emoji: string;
  user_id: string;
  user_name: string;
}

interface LiveChatSession {
  id: string;
  customer: ChatUser;
  technician?: ChatUser;
  status: 'waiting' | 'active' | 'resolved' | 'closed' | 'transferred';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  category: 'technical' | 'billing' | 'general' | 'emergency';
  subject: string;
  motor?: string;
  configuration?: string;
  created_at: string;
  started_at?: string;
  ended_at?: string;
  messages: ChatMessage[];
  tags: string[];
  satisfaction_rating?: number;
  notes?: string;
  queue_position?: number;
  estimated_wait_time?: number;
}

// ===============================================
// 📊 COMPONENTE PRINCIPAL
// ===============================================

const LiveChat: React.FC<{ stats?: any; onStatsUpdate?: (stats: any) => void }> = ({ 
  stats, 
  onStatsUpdate 
}) => {
  const { isDark } = useTheme();
  const [chatSessions, setChatSessions] = useState<LiveChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<LiveChatSession | null>(null);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<ChatUser[]>([]);
  const [notifications, setNotifications] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showUserList, setShowUserList] = useState(false);
  const [loading, setLoading] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ===============================================
  // 🎨 CLASES DE ESTILO
  // ===============================================

  const cardClasses = `rounded-lg shadow-md transition-all duration-200 ${
    isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
  }`;

  const textClasses = isDark ? 'text-white' : 'text-gray-800';
  const subTextClasses = isDark ? 'text-gray-400' : 'text-gray-600';
  const borderClasses = isDark ? 'border-gray-600' : 'border-gray-300';

  // ===============================================
  // 🔄 EFECTOS Y CARGA DE DATOS
  // ===============================================

  useEffect(() => {
    loadChatSessions();
    loadOnlineUsers();
    
    // Simular actualizaciones en tiempo real
    const interval = setInterval(() => {
      updateChatSessions();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [activeSession?.messages]);

  const loadChatSessions = async () => {
    setLoading(true);
    try {
      // Simulación de sesiones de chat
      const mockSessions: LiveChatSession[] = [
        {
          id: '1',
          customer: {
            id: 'c1',
            name: 'Juan Pérez',
            email: 'juan.perez@empresa.com',
            status: 'online',
            role: 'customer'
          },
          technician: {
            id: 't1',
            name: 'María González',
            email: 'maria.gonzalez@cat.com',
            status: 'online',
            role: 'technician'
          },
          status: 'active',
          priority: 'high',
          category: 'technical',
          subject: 'Problema urgente con motor C27',
          motor: 'C27',
          configuration: 'L4G',
          created_at: new Date(Date.now() - 1800000).toISOString(),
          started_at: new Date(Date.now() - 1200000).toISOString(),
          messages: [
            {
              id: 'm1',
              chat_id: '1',
              sender_id: 'c1',
              sender_name: 'Juan Pérez',
              sender_role: 'customer',
              content: 'Hola, tengo un problema urgente con mi motor C27 L4G. El motor está perdiendo potencia y hace ruidos extraños.',
              type: 'text',
              timestamp: new Date(Date.now() - 1200000).toISOString(),
              is_read: true
            },
            {
              id: 'm2',
              chat_id: '1',
              sender_id: 't1',
              sender_name: 'María González',
              sender_role: 'technician',
              content: '¡Hola Juan! Soy María, técnica especializada en motores C27. Te voy a ayudar con este problema. ¿Podrías decirme cuántas horas de operación tiene el motor?',
              type: 'text',
              timestamp: new Date(Date.now() - 1080000).toISOString(),
              is_read: true
            },
            {
              id: 'm3',
              chat_id: '1',
              sender_id: 'c1',
              sender_name: 'Juan Pérez',
              sender_role: 'customer',
              content: 'El motor tiene aproximadamente 2,500 horas. El problema empezó ayer durante la operación normal.',
              type: 'text',
              timestamp: new Date(Date.now() - 900000).toISOString(),
              is_read: true
            },
            {
              id: 'm4',
              chat_id: '1',
              sender_id: 't1',
              sender_name: 'María González',
              sender_role: 'technician',
              content: 'Perfecto. Con 2,500 horas es posible que necesite mantenimiento. ¿Podrías enviarme un video del ruido que está haciendo? Eso me ayudaría mucho para el diagnóstico.',
              type: 'text',
              timestamp: new Date(Date.now() - 600000).toISOString(),
              is_read: true
            }
          ],
          tags: ['urgente', 'C27', 'pérdida de potencia'],
          satisfaction_rating: undefined
        },
        {
          id: '2',
          customer: {
            id: 'c2',
            name: 'Carlos Rodriguez',
            email: 'carlos.rodriguez@minera.com',
            status: 'online',
            role: 'customer'
          },
          status: 'waiting',
          priority: 'normal',
          category: 'technical',
          subject: 'Consulta sobre mantenimiento C175',
          motor: 'C175',
          configuration: 'AJ8',
          created_at: new Date(Date.now() - 300000).toISOString(),
          messages: [
            {
              id: 'm5',
              chat_id: '2',
              sender_id: 'system',
              sender_name: 'Sistema',
              sender_role: 'system',
              content: 'Carlos Rodriguez se ha unido al chat. Motivo: Consulta sobre mantenimiento C175',
              type: 'system',
              timestamp: new Date(Date.now() - 300000).toISOString(),
              is_read: true
            },
            {
              id: 'm6',
              chat_id: '2',
              sender_id: 'c2',
              sender_name: 'Carlos Rodriguez',
              sender_role: 'customer',
              content: 'Hola, necesito información sobre el cronograma de mantenimiento para mi motor C175 AJ8. ¿Pueden ayudarme?',
              type: 'text',
              timestamp: new Date(Date.now() - 295000).toISOString(),
              is_read: false
            }
          ],
          tags: ['mantenimiento', 'C175'],
          queue_position: 1,
          estimated_wait_time: 3
        },
        {
          id: '3',
          customer: {
            id: 'c3',
            name: 'Ana Martinez',
            email: 'ana.martinez@construccion.com',
            status: 'online',
            role: 'customer'
          },
          status: 'waiting',
          priority: 'low',
          category: 'general',
          subject: 'Información sobre piezas de repuesto',
          created_at: new Date(Date.now() - 600000).toISOString(),
          messages: [
            {
              id: 'm7',
              chat_id: '3',
              sender_id: 'system',
              sender_name: 'Sistema',
              sender_role: 'system',
              content: 'Ana Martinez se ha unido al chat. Motivo: Información sobre piezas de repuesto',
              type: 'system',
              timestamp: new Date(Date.now() - 600000).toISOString(),
              is_read: true
            }
          ],
          tags: ['piezas', 'repuestos'],
          queue_position: 2,
          estimated_wait_time: 8
        }
      ];

      setChatSessions(mockSessions);
      
      // Seleccionar primera sesión activa por defecto
      const activeSessionDefault = mockSessions.find(session => session.status === 'active');
      if (activeSessionDefault) {
        setActiveSession(activeSessionDefault);
      }
    } catch (error) {
      console.error('Error cargando sesiones de chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOnlineUsers = async () => {
    // Simulación de usuarios online
    const mockUsers: ChatUser[] = [
      {
        id: 't1',
        name: 'María González',
        email: 'maria.gonzalez@cat.com',
        status: 'online',
        role: 'technician'
      },
      {
        id: 't2',
        name: 'Roberto Silva',
        email: 'roberto.silva@cat.com',
        status: 'busy',
        role: 'technician'
      },
      {
        id: 't3',
        name: 'Laura Chen',
        email: 'laura.chen@cat.com',
        status: 'away',
        role: 'supervisor'
      }
    ];

    setOnlineUsers(mockUsers);
  };

  const updateChatSessions = () => {
    // Simular actualizaciones en tiempo real
    if (Math.random() > 0.7) {
      // Simular nuevo mensaje ocasional
      // En implementación real, esto vendría de WebSocket
    }
  };

  // ===============================================
  // 🎯 FUNCIONES DE CHAT
  // ===============================================

  const sendMessage = async () => {
    if (!currentMessage.trim() || !activeSession) return;

    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      chat_id: activeSession.id,
      sender_id: 't1', // ID del técnico actual
      sender_name: 'María González',
      sender_role: 'technician',
      content: currentMessage.trim(),
      type: 'text',
      timestamp: new Date().toISOString(),
      is_read: false
    };

    // Actualizar sesión activa
    const updatedSession = {
      ...activeSession,
      messages: [...activeSession.messages, newMessage]
    };

    setActiveSession(updatedSession);
    
    // Actualizar lista de sesiones
    setChatSessions(prev => 
      prev.map(session => 
        session.id === activeSession.id ? updatedSession : session
      )
    );

    setCurrentMessage('');
    
    // Reproducir sonido si está habilitado
    if (soundEnabled) {
      playMessageSound();
    }
  };

  const acceptChat = (sessionId: string) => {
    setChatSessions(prev =>
      prev.map(session =>
        session.id === sessionId
          ? {
              ...session,
              status: 'active',
              started_at: new Date().toISOString(),
              technician: {
                id: 't1',
                name: 'María González',
                email: 'maria.gonzalez@cat.com',
                status: 'online',
                role: 'technician'
              }
            }
          : session
      )
    );
  };

  const transferChat = (sessionId: string, technicianId: string) => {
    // Implementar transferencia de chat
    console.log(`Transferir chat ${sessionId} a técnico ${technicianId}`);
  };

  const closeChat = (sessionId: string, rating?: number, notes?: string) => {
    setChatSessions(prev =>
      prev.map(session =>
        session.id === sessionId
          ? {
              ...session,
              status: 'closed',
              ended_at: new Date().toISOString(),
              satisfaction_rating: rating,
              notes: notes
            }
          : session
      )
    );
    
    if (activeSession?.id === sessionId) {
      setActiveSession(null);
    }
  };

  const playMessageSound = () => {
    // Implementar reproducción de sonido
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+D3y3kpBSl+zO/aizsIGGS+8OOYTgwOUarm7blCGgU7k9n00H0vBSF0xe3ajzwKGV247s1UGAg+ltryxnkpBSh+zPDaiD0IGGa...'); // Placeholder
    audio.volume = 0.3;
    audio.play().catch(() => {});
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ===============================================
  // 🎨 FUNCIONES DE RENDERIZADO
  // ===============================================

  const getStatusColor = (status: string) => {
    const colors = {
      online: 'bg-green-500',
      away: 'bg-yellow-500',
      busy: 'bg-red-500',
      offline: 'bg-gray-500'
    };
    return colors[status as keyof typeof colors] || colors.offline;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: isDark ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800',
      normal: isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800',
      high: isDark ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800',
      urgent: isDark ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
    };
    return colors[priority as keyof typeof colors] || colors.normal;
  };

  const getSessionStatusIcon = (status: string) => {
    const icons = {
      waiting: <Clock className="w-4 h-4 text-yellow-600" />,
      active: <MessageCircle className="w-4 h-4 text-green-600" />,
      resolved: <CheckCircle className="w-4 h-4 text-blue-600" />,
      closed: <X className="w-4 h-4 text-gray-600" />,
      transferred: <Share2 className="w-4 h-4 text-purple-600" />
    };
    return icons[status as keyof typeof icons] || icons.waiting;
  };

  // ===============================================
  // 🎨 RENDER
  // ===============================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center space-x-3">
          <MessageCircle className="w-6 h-6 animate-pulse text-blue-600" />
          <span className={textClasses}>Cargando sistema de chat...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] flex">
      
      {/* ✅ SIDEBAR - LISTA DE CHATS */}
      <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        
        {/* Header del sidebar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className={`font-semibold ${textClasses}`}>
              Chats en Vivo ({chatSessions.length})
            </h3>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                title={soundEnabled ? 'Desactivar sonido' : 'Activar sonido'}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              
              <button
                onClick={() => setShowUserList(!showUserList)}
                className={`p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                title="Ver técnicos online"
              >
                <Users className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Filtros rápidos */}
          <div className="flex space-x-1">
            <button className="px-2 py-1 rounded text-xs bg-blue-600 text-white">
              Todos ({chatSessions.length})
            </button>
            <button className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
              Esperando ({chatSessions.filter(s => s.status === 'waiting').length})
            </button>
            <button className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
              Activos ({chatSessions.filter(s => s.status === 'active').length})
            </button>
          </div>
        </div>

        {/* Lista de sesiones */}
        <div className="flex-1 overflow-y-auto">
          {chatSessions.map((session) => (
            <div
              key={session.id}
              onClick={() => setActiveSession(session)}
              className={`p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer transition-colors ${
                activeSession?.id === session.id
                  ? isDark ? 'bg-blue-900' : 'bg-blue-50'
                  : isDark ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start space-x-3">
                {/* Avatar y estado */}
                <div className="relative">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-gray-700' : 'bg-gray-200'
                  }`}>
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 ${
                    isDark ? 'border-gray-800' : 'border-white'
                  } ${getStatusColor(session.customer.status)}`}></div>
                </div>

                {/* Información de la sesión */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className={`font-medium truncate ${textClasses}`}>
                      {session.customer.name}
                    </h4>
                    <div className="flex items-center space-x-1">
                      {getSessionStatusIcon(session.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(session.priority)}`}>
                        {session.priority}
                      </span>
                    </div>
                  </div>
                  
                  <p className={`text-sm ${subTextClasses} truncate mb-1`}>
                    {session.subject}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs">
                    <span className={subTextClasses}>
                      {session.motor && `${session.motor} ${session.configuration || ''}`}
                    </span>
                    <span className={subTextClasses}>
                      {session.status === 'waiting' && session.queue_position && (
                        `Cola: ${session.queue_position} (${session.estimated_wait_time}min)`
                      )}
                      {session.status === 'active' && 'En curso'}
                      {session.status === 'closed' && 'Finalizado'}
                    </span>
                  </div>

                  {/* Último mensaje */}
                  {session.messages.length > 0 && (
                    <div className={`text-xs ${subTextClasses} mt-2 truncate`}>
                      <span className="font-medium">
                        {session.messages[session.messages.length - 1].sender_name}:
                      </span>{' '}
                      {session.messages[session.messages.length - 1].content}
                    </div>
                  )}
                </div>
              </div>

              {/* Acciones rápidas */}
              <div className="mt-3 flex space-x-2">
                {session.status === 'waiting' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      acceptChat(session.id);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs"
                  >
                    Aceptar
                  </button>
                )}
                
                {session.status === 'active' && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        transferChat(session.id, 't2');
                      }}
                      className={`px-3 py-1 rounded text-xs ${
                        isDark 
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      Transferir
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        closeChat(session.id);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
                    >
                      Cerrar
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ ÁREA DE CHAT PRINCIPAL */}
      <div className="flex-1 flex flex-col">
        {activeSession ? (
          <>
            {/* Header del chat */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-gray-700' : 'bg-gray-200'
                    }`}>
                      <User className="w-5 h-5 text-gray-500" />
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 ${
                      isDark ? 'border-gray-800' : 'border-white'
                    } ${getStatusColor(activeSession.customer.status)}`}></div>
                  </div>
                  
                  <div>
                    <h3 className={`font-semibold ${textClasses}`}>
                      {activeSession.customer.name}
                    </h3>
                    <p className={`text-sm ${subTextClasses}`}>
                      {activeSession.subject}
                      {activeSession.motor && ` • ${activeSession.motor} ${activeSession.configuration || ''}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    className={`p-2 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    title="Llamada de voz"
                  >
                    <Phone className="w-5 h-5" />
                  </button>
                  
                  <button
                    className={`p-2 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    title="Videollamada"
                  >
                    <Video className="w-5 h-5" />
                  </button>
                  
                  <button
                    className={`p-2 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    title="Más opciones"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Mensajes */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {activeSession.messages.map((message) => (
                <div key={message.id} className="flex items-start space-x-3">
                  
                  {/* Avatar del remitente */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.sender_role === 'technician'
                      ? 'bg-blue-600 text-white'
                      : message.sender_role === 'system'
                        ? 'bg-gray-500 text-white'
                        : isDark ? 'bg-gray-700' : 'bg-gray-200'
                  }`}>
                    {message.sender_role === 'technician' ? (
                      <Headphones className="w-4 h-4" />
                    ) : message.sender_role === 'system' ? (
                      <Settings className="w-4 h-4" />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                  </div>

                  {/* Contenido del mensaje */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`text-sm font-medium ${textClasses}`}>
                        {message.sender_name}
                      </span>
                      <span className={`text-xs ${subTextClasses}`}>
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </span>
                      {message.sender_role === 'technician' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Técnico
                        </span>
                      )}
                    </div>
                    
                    <div className={`inline-block max-w-3xl p-3 rounded-lg ${
                      message.sender_role === 'technician'
                        ? isDark ? 'bg-blue-900' : 'bg-blue-600 text-white'
                        : message.sender_role === 'system'
                          ? isDark ? 'bg-gray-700' : 'bg-gray-100'
                          : isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <div className="whitespace-pre-line">
                        {message.content}
                      </div>
                      
                      {/* Archivos adjuntos */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {message.attachments.map((attachment, index) => (
                            <div
                              key={index}
                              className={`flex items-center px-2 py-1 rounded text-xs ${
                                isDark ? 'bg-gray-600' : 'bg-gray-200 text-gray-700'
                              }`}
                            >
                              <Paperclip className="w-3 h-3 mr-1" />
                              {attachment.filename}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Estado de lectura */}
                    {message.sender_role === 'technician' && (
                      <div className="flex items-center justify-end mt-1">
                        {message.is_read ? (
                          <CheckCircle className="w-3 h-3 text-blue-600" />
                        ) : (
                          <Clock className="w-3 h-3 text-gray-400" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Indicador de escritura */}
              {isTyping && (
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-gray-700' : 'bg-gray-200'
                  }`}>
                    <User className="w-4 h-4" />
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input de mensaje */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-end space-x-3">
                
                {/* Campo de texto */}
                <div className="flex-1">
                  <textarea
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Escribe tu respuesta..."
                    className={`w-full px-4 py-2 border rounded-lg resize-none ${borderClasses} ${
                      isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'
                    }`}
                    rows={Math.min(3, Math.max(1, currentMessage.split('\n').length))}
                  />
                </div>

                {/* Botones de acción */}
                <div className="flex space-x-2">
                  
                  {/* Adjuntar archivo */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`p-2 rounded-lg transition-colors ${
                      isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                    title="Adjuntar archivo"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>

                  {/* Enviar */}
                  <button
                    onClick={sendMessage}
                    disabled={!currentMessage.trim()}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white p-2 rounded-lg transition-colors"
                    title="Enviar mensaje"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Input oculto para archivos */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  // Manejar carga de archivos
                  const files = Array.from(e.target.files || []);
                  console.log('Archivos seleccionados:', files);
                }}
              />
            </div>
          </>
        ) : (
          /* Estado vacío - sin chat seleccionado */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className={`text-lg font-medium ${textClasses} mb-2`}>
                Selecciona un chat
              </h3>
              <p className={subTextClasses}>
                Elige una conversación de la lista para comenzar a chatear
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ✅ PANEL LATERAL DE USUARIOS (Opcional) */}
      {showUserList && (
        <div className="w-64 border-l border-gray-200 dark:border-gray-700 p-4">
          <h4 className={`font-semibold ${textClasses} mb-4`}>
            Técnicos Online ({onlineUsers.length})
          </h4>
          
          <div className="space-y-3">
            {onlineUsers.map((user) => (
              <div key={user.id} className="flex items-center space-x-3">
                <div className="relative">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-gray-700' : 'bg-gray-200'
                  }`}>
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 ${
                    isDark ? 'border-gray-800' : 'border-white'
                  } ${getStatusColor(user.status)}`}></div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${textClasses} truncate`}>
                    {user.name}
                  </p>
                  <p className={`text-xs ${subTextClasses} truncate`}>
                    {user.role} • {user.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveChat;